import { SetContextLink } from "@apollo/client/link/context";
import stringify from "json-stable-stringify";

/**
 * Computes a SHA-256 hash of the mutation operation + variables and attaches
 * it as the `X-Request-Hash` HTTP header. Paired with `idempotencyLink` (which
 * sets `Idempotency-Key`): the backend stores both on each new payment row,
 * and a later call that presents the same key with a *different* hash is
 * rejected as `IDEMPOTENCY_CONFLICT` rather than silently returning the
 * prior result.
 *
 * Sits behind `RetryLink` so retries of the same operation produce an
 * identical hash (same operation name + same variables → same SHA-256).
 *
 * No-op for queries and subscriptions - the backend only inspects the header
 * on mutations that opt into idempotency dedup.
 *
 * `crypto.subtle.digest` is async, which is fine - `SetContextLink` accepts
 * async callbacks in Apollo Client v4. The hash itself is ~0.1ms for typical
 * mutation payloads, well below network jitter.
 */
const sha256Hex = async (input: string): Promise<string> => {
    const buffer = new TextEncoder().encode(input);
    const hash = await crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
};

// Canonical JSON via `json-stable-stringify` - same library the backend
// uses for audit-log diffs (`triggerLogUtils.ts`), so the serialization
// is identical on both sides if we ever need to recompute a hash to
// verify the client's request. Handles edge cases the hand-rolled
// version missed (undefined-valued object keys are omitted per the JSON
// spec instead of concatenated as the literal string "undefined").
export const requestHashLink = new SetContextLink(async ({ headers }, operation) => {
    const isMutation = operation.query.definitions.some(
        (d) => d.kind === "OperationDefinition" && d.operation === "mutation",
    );
    if (!isMutation) {
        return { headers };
    }
    const payload = stringify({
        op: operation.operationName,
        vars: operation.variables ?? {},
    });
    const hash = await sha256Hex(payload);
    return {
        headers: {
            ...headers,
            "X-Request-Hash": hash,
        },
    };
});
